import { inject, Injectable } from '@angular/core';
import padWithZeros from '@core/utils/padWithZeros';
import {
  CoordsSelector,
  RealtimeIsActiveSelector,
  SetVariablesDataAction,
  SetVariablesPayloadType,
  ToDateSelector,
} from '@dashboard/dashboard-store';
import { IngestionErrorEvaluationData } from '@dashboard/dashboard-store/selectors/system-errors.selector';
import { Store } from '@ngrx/store';
import moment, { Moment } from 'moment';
import { firstValueFrom, Subscription, timer } from 'rxjs';
import { EnvVarsService } from '../../../../env-vars.service';
import { DateToItsTimezone } from '@core/constants/time.defaults';

@Injectable()
export class SystemDataErrorsService {
  private _store = inject(Store);
  private environment = inject(EnvVarsService);

  private VESSEL_OFFLINE_TIMEOUT_HOURS =
    this.environment.getEnvKey('VESSEL_OFFLINE_TIMEOUT_HOURS') || 2;
  private $IngestionTimer: Subscription;

  public InitIngestionErrorChecker() {
    console.debug('Ingestion Error Timer Started');

    // const rangeToEndOfSeocondMin =
    //   moment().endOf('minute').diff(moment())
    const TenSecsInMs = 10_000;
    const AnHourMs = 3_600_000;

    this.$IngestionTimer = timer(TenSecsInMs, AnHourMs).subscribe(() => {
      this.IngestionErrorChecker();
    });
  }

  public stopIngestionErrorChecker() {
    this.$IngestionTimer.unsubscribe();
  }

  public async IngestionErrorChecker() {
    const vesselsData = await firstValueFrom(
      this._store.select(IngestionErrorEvaluationData)
    );
    const coordsPerVes = await firstValueFrom(
      this._store.select(CoordsSelector)
    );
    const selectedDate = await firstValueFrom(
      this._store.select(ToDateSelector)
    );

    const isRealTime = await firstValueFrom(
      this._store.select(RealtimeIsActiveSelector)
    );

    const variableMeta = vesselsData
      .map(({ imo, systemHealth, latest_timestamp }) => {
        const latestStateImoHasError = this.IgestionHasErrorRule(
          isRealTime ? DateToItsTimezone(moment(), 0) : selectedDate,
          systemHealth,
          latest_timestamp * 1000
        );

        const { timestamp: coordTimestamp, formattedValue: coordValue } =
          coordsPerVes[imo];

        const coordHasError = this.IgestionHasErrorRule(
          isRealTime ? DateToItsTimezone(moment(), 0) : selectedDate,
          'Good',
          coordTimestamp * 1000
        );

        const coordHasValue =
          coordValue !== null &&
          coordValue !== undefined &&
          coordValue !== '' &&
          coordValue !== 'N/A';

        const errorDescription = this.produceIngestionErrorDescription(
          systemHealth,
          latestStateImoHasError,
          coordHasValue,
          coordHasError
        );

        const stateTimestampValidation = {
          hasError: latestStateImoHasError || !coordHasValue || coordHasError,
          imo: imo,
          description: errorDescription,
        };
        return stateTimestampValidation;
      })
      .map(({ imo, hasError, description }) => {
        const payload: SetVariablesPayloadType = [
          padWithZeros(imo, 8),
          'ingestion_error',
          {
            notInSync: false,
            variableId: 'ingestion_error',
            timestamp: moment().valueOf() / 1000,
            formattedValue: `${hasError}`,
            description,
          },
        ];
        return payload;
      });

    this._store.dispatch(
      SetVariablesDataAction({ variableMeta, source: 'API' })
    );
  }

  private produceIngestionErrorDescription(
    systemHealth: string,
    latestStateNotSync: boolean,
    coordHasValue: boolean,
    coordNotInSync: boolean
  ) {
    const errorDescriptions: string[] = [];

    if (latestStateNotSync) {
      errorDescriptions.push(
        'Latest system health data is outdated or System is offline'
      );
    }

    if (!coordHasValue) {
      errorDescriptions.push('No coordinate data available');
    }

    if (coordNotInSync) {
      errorDescriptions.push('Coordinate data is outdated');
    }
    return errorDescriptions.join(';');
  }

  private IgestionHasErrorRule(
    selectedDate: string,
    systemHealth: string,
    state_timestamp_ms: number
  ) {
    /**
     * if we log these selectedDate & state tiemstamp
     * we see that the timestamp and the date are already formatted. but the date has +iso
     *so we need to ignore these.
    1744029120000  Mon Apr 07 2025 12:32:00.000
2025-04-07T12:32:13+0100
     */
    /**
     * state_timestamp_ms is in UTC.
     * selectedDate has a timezone, but we want to ignore it and treat it as naive UTC.
     */

    const StateTimestampUtc = moment.utc(state_timestamp_ms);

    // ❗️Ignore the timezone part completely and treat it as UTC time
    const strippedDate = selectedDate.replace(/([+-]\d{4})$/, '');
    const selectDateUtc = moment.utc(strippedDate, 'YYYY-MM-DDTHH:mm:ss');

    const range = selectDateUtc.diff(StateTimestampUtc, 'hours', true);

    const HoursArePassed = Math.abs(range) > this.VESSEL_OFFLINE_TIMEOUT_HOURS;

    const statusIsOffline = systemHealth === 'offline';

    return HoursArePassed || statusIsOffline;
  }
}
